import type { JatraView } from '@aj/core';
import { Box, Chip, HStack, Text, VStack } from '@aj/ui';

import { useFlavour } from '../../data/hooks.js';
import { rankKey, titleKey } from '../../lib/labels.js';
import { useT } from '../../providers/I18nProvider.js';

/**
 * How far you have come — progression as a route, not a score.
 *
 * This is the answer to "levelling up like a game" that does not become the
 * scoreboard §53.8 warns about. A path ONLY EVER GROWS: stops behind you are
 * things that happened, and no fortnight of missed days can walk you back
 * down it. A big XP numeral has the opposite property — it is a live score,
 * and a live score is the thing that collapses when the streak breaks.
 *
 * The next stop is NAMED, with a distance to it. "340 to Vanguard" is the
 * unit that actually pulls; "2,340 power" is a number about nothing.
 */
export function JourneyPath({ view }: { view: JatraView }) {
  const t = useT();
  const flavour = useFlavour();
  const { path } = view;

  return (
    <VStack gap={3}>
      <Text variant="overline" tone="subtle">
        {t('jatra.pathTitle')}
      </Text>

      {/* Horizontal scroll rather than shrinking: eight stops cannot fit a
          phone legibly, and a squeezed route reads as a progress bar, which
          is exactly the thing it is not. */}
      <Box className="overflow-x-auto">
        <HStack gap={0} align="start" className="w-max pb-1">
          {path.stops.map((stop, index) => (
            <HStack key={stop.id} gap={0} align="start">
              {index > 0 && (
                <Box
                  aria-hidden
                  className={`mt-[7px] h-0.5 w-10 shrink-0 ${
                    stop.state === 'ahead' ? 'bg-line' : 'bg-gold'
                  }`}
                />
              )}
              <VStack gap={1} align="center" className="w-20 shrink-0">
                {/* Shape as well as colour: the current stop is a ring, passed
                    stops are filled, stops ahead are hollow and faint. One hue
                    cannot carry three states for everyone. */}
                <Box
                  aria-hidden
                  className={
                    stop.state === 'passed'
                      ? 'size-3 rounded-full bg-gold'
                      : stop.state === 'current'
                        ? 'size-4 rounded-full border-2 border-gold bg-surface'
                        : 'size-3 rounded-full border border-line bg-surface'
                  }
                />
                <Text
                  variant="caption"
                  tone={stop.state === 'ahead' ? 'subtle' : 'ink'}
                  className="text-center leading-tight"
                >
                  {t(rankKey(flavour, stop.id))}
                </Text>
              </VStack>
            </HStack>
          ))}
        </HStack>
      </Box>

      <Text variant="caption" tone="muted" numeric>
        {path.next === null
          ? t('jatra.pathEnd')
          : t('jatra.pathNext', {
              count: path.next.remaining,
              rank: t(rankKey(flavour, path.next.id)),
            })}
      </Text>

      {/* What was collected along the way. Dated, because a title with a date
          is a memory and a title without one is a badge. */}
      {view.titles.length > 0 && (
        <VStack gap={2}>
          <Text variant="overline" tone="subtle">
            {t('jatra.alongTheWay')}
          </Text>
          <HStack gap={2} className="flex-wrap">
            {view.titles.map((title) => (
              <Chip key={title} tone="neutral">
                {t(titleKey(flavour, title))}
              </Chip>
            ))}
          </HStack>
        </VStack>
      )}

      {view.streak.best > 0 && (
        <Text variant="caption" tone="subtle" numeric>
          {t('jatra.streakBest', { count: view.streak.best })}
        </Text>
      )}
    </VStack>
  );
}
